import { clsx } from 'clsx';
import { useRef } from 'react';

import { useGetMainNumbersData, useGetPrettyNumbersData } from '@/api';
import { TariffFeature } from '@/api/enums/TariffFeature.ts';
import { TariffResponseItem } from '@/api/types';
import { DeviceBadge } from '@/components/shared/DeviceBadge/DeviceBadge.tsx';
import { useChosenNumberStore, useModalStore } from '@/store';
import { ChosenNumber } from '@/types';
import { getEnumKeyByValue } from '@/utils';

import cn from './styles.module.scss';

interface ITariffBlockProps {
    onClick?: (chosenNumber: ChosenNumber) => void;
    activationFee: number;
    tariffName: string;
    tariffPrice: number;
    tariffMin: string;
    tariffInternet: string;
    tariffSms: string;
    tariffSid: string;
    tariffs: string[];
    minTariffPrice: number;
    properties: TariffResponseItem['options'];
    number: string;
    region: string;
    operator: string;
    deviceType: string;
    numberCost: number;
}

export const TariffBlock = ({
    onClick,
    activationFee,
    tariffInternet,
    tariffMin,
    tariffName,
    tariffPrice,
    tariffSms,
    tariffSid,
    tariffs,
    minTariffPrice,
    properties,
    number,
    region,
    operator,
    deviceType,
    numberCost,
}: ITariffBlockProps) => {
    const { isReservationOpen } = useModalStore();
    const {
        activeTariffBlockRef,
        setActiveTariffBlockRef,
        activeTariffBlockModalRef,
        setActiveTariffBlockModalRef,
        setActiveTariffSid,
        clearOnUnmount,
    } = useChosenNumberStore();
    const { data: mainTariffsData } = useGetMainNumbersData();
    const { data: prettyNumsTariffsData } = useGetPrettyNumbersData();

    const data = mainTariffsData || prettyNumsTariffsData;

    const tariffBlockRef = useRef<HTMLDivElement>(null);

    const handleClick = () => {
        if (!isReservationOpen) {
            if (onClick) {
                onClick({
                    activationFee,
                    operator,
                    tariff: tariffName,
                    number: number,
                    region: region,
                    price: tariffPrice,
                    tariffSid,
                    tariffMin,
                    tariffs,
                    minTariffPrice,
                    tariffInternet,
                    tariffSms,
                    deviceType,
                    numberCost,
                });
            }

            setActiveTariffBlockRef(
                activeTariffBlockRef === tariffBlockRef ? null : tariffBlockRef
            );

            if (activeTariffBlockRef === tariffBlockRef) {
                clearOnUnmount();
            }
        } else {
            setActiveTariffBlockModalRef(tariffBlockRef);
        }

        setActiveTariffSid(tariffSid);
    };

    return (
        <div
            ref={tariffBlockRef}
            onClick={handleClick}
            className={clsx(
                cn.tariffContainer,
                (activeTariffBlockRef === tariffBlockRef ||
                    activeTariffBlockModalRef === tariffBlockRef) &&
                    cn.active
            )}
        >
            <div className={cn.deviceType}>
                <DeviceBadge device={deviceType} />
            </div>
            <div className={cn.tariffInfo}>
                <p>{tariffName}</p>
                <p>{tariffPrice} руб/мес</p>
                <p>
                    {tariffMin && <span>{tariffMin} мин, </span>}
                    <span>{tariffInternet}</span>
                    {tariffSms && <span>, {tariffSms} смс</span>}
                </p>
            </div>
            {properties?.length > 0 && (
                <div className={cn.tariffOptions}>
                    {properties.map(item => {
                        const tariffOption = data?.tariffOptions?.find(
                            option => option.id === item
                        );
                        const optionIconName = getEnumKeyByValue(
                            TariffFeature,
                            item
                        )?.toLowerCase();
                        return (
                            <div key={item} className={cn.tariffOptionsItem}>
                                <img src={`/images/icons/features/${optionIconName}.svg`} alt="" />
                                {tariffOption?.name}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};
